import React, { useCallback } from 'react';
import classNames from 'classnames/bind';
import DropdownLoader from '../../../../DropdownLoader/DropdownLoader';
import RetryMenuItem from '../../../../RetryMenuItem/RetryMenuItem';
import { DEFAULT_ITEM_HEIGHT, EIGHTY_VH_RATIO, FILTER_TABS_HEIGHT, SEARCH_INPUT_HEIGHT } from '../pickerConstatns';
import styles from '../BreadcrumbsDropdown.module.scss';

const DefaultOption = ({ data, idValue }) => <span>{idValue ? data[idValue] : data?.value}</span>;
const classNameBuilder = classNames.bind(styles);

export const useDropdownRenderer = ({
    options = [],
    highlightedIndex,
    hasNextPage,
    ItemComponent,
    innerProps,
    onSelect,
    closeDropdown,
    selectedItem,
    updateItemHeight,
    listRef,
    hasChildren,
    itemHeights,
    error,
    isLoading,
    isFetchingNextPage,
    refetch,
    ...rest
}) => {
    const hasErrorOrLoading = error || isLoading || isFetchingNextPage;

    const getListHeightWithVhConstraint = useCallback(() => {
        const maxDropdownHeight = window.innerHeight * EIGHTY_VH_RATIO;
        const filterTabsHeight = hasChildren ? FILTER_TABS_HEIGHT : 0;
        const maxAvailableHeight = maxDropdownHeight - filterTabsHeight - SEARCH_INPUT_HEIGHT;
        let actualContentHeight = 0;
        const visibleItemCount = options?.length || 0;

        const totalItemCount = visibleItemCount + (hasErrorOrLoading ? 1 : 0);

        if (itemHeights && itemHeights.current && itemHeights.current.size > 0) {
            for (let i = 0; i < totalItemCount; i++) {
                actualContentHeight += itemHeights.current.get(i) || DEFAULT_ITEM_HEIGHT;
            }
        } else {
            actualContentHeight = totalItemCount * DEFAULT_ITEM_HEIGHT;
        }

        const finalHeight = Math.min(actualContentHeight, maxAvailableHeight);
        return Math.max(DEFAULT_ITEM_HEIGHT, finalHeight);
    }, [options, itemHeights, hasChildren, hasErrorOrLoading]);

    const handleOptionClick = useCallback(
        (option, e) => {
            try {
                const optionCopy = { ...option };

                if (innerProps?.onClick) {
                    innerProps.onClick(optionCopy, e);
                }
                if (onSelect) {
                    onSelect(optionCopy);
                }
                closeDropdown();
            } catch (clickError) {
                console.error('Error in option click handler:', clickError);
            }
        },
        [innerProps, onSelect, closeDropdown]
    );

    const itemCount = hasNextPage ? options.length + 1 : options.length;
    const isItemLoaded = index => hasErrorOrLoading || !hasNextPage || index < options.length;

    const ItemComponentRenderer = useCallback(
        ({ index, style }) => {
            const isLastItem = index === itemCount - 1;
            if (error && isLastItem) {
                return (
                    <div style={style}>
                        <RetryMenuItem onRetry={refetch} />
                    </div>
                );
            }

            if (!error && (isLoading || isFetchingNextPage) && isLastItem) {
                return (
                    <div style={style}>
                        <DropdownLoader />
                    </div>
                );
            }

            if (index < 0 || index >= options.length) {
                return null;
            }

            const option = options[index];
            if (!option) {
                return null;
            }

            const hasMinHeight = option?.id !== -1;
            return (
                <div
                    style={style}
                    className={classNameBuilder('dropdown-option', {
                        'dropdown-option-with-min-height': hasMinHeight,
                        highlightedIndex: highlightedIndex === index,
                    })}
                    onClick={e => handleOptionClick(option, e)}
                >
                    {ItemComponent ? (
                        ItemComponent({
                            data: option,
                            getValue: () => selectedItem,
                            updateItemHeight,
                            listRef,
                            index,
                            ...rest,
                        })
                    ) : (
                        <DefaultOption data={option} {...rest} />
                    )}
                </div>
            );
        },
        [
            options,
            highlightedIndex,
            handleOptionClick,
            ItemComponent,
            selectedItem,
            updateItemHeight,
            listRef,
            error,
            isLoading,
            isFetchingNextPage,
            refetch,
            itemCount,
            rest,
        ]
    );

    return {
        ItemComponentRenderer,
        getListHeightWithVhConstraint,
        isItemLoaded,
        itemCount,
    };
};
